import { ChangeDetectionStrategy, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CartItemContext, OrderEntry } from '@spartacus/cart/base/root';
import { CartItemContextSource } from '@spartacus/cart/base/components';
import { CustomCartItemComponent } from '../cart-item/cart-item.component';
import { BehaviorSubject, of, Subscription, take } from 'rxjs';
import { Product } from '@spartacus/core';
import { Warehouse } from '../../../../../product/product-inventory/core/product-inventory.model';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ModalService } from '../../../../../../../components/modal-list/modal-list.service';
import { ModalSeeNotesComponent } from '../../modal-see-notes/modal-see-notes.component';
import { CartPoLineService } from '../../cart-po-line/core/cart-po-line.service';
import { CustomActiveCartService } from '../../../../_core/active-cart.service';
import { ToasterService } from '../../../../../../../components/toaster/toaster.service';
import { TOASTER_TYPE } from '../../../../../../../components/toaster/toaster.model';
import { fixProductName, isDate, isEmpty } from '../../../../../../../core/services/simple-tools';
import { DEVICE_SIZE } from '../../../../../../../core/models/others.model';
import { ModalConfigDetailsComponent } from '../../modal-config-details/modal-config-details.component';

@Component({
  selector: '[cx-cart-item-list-row], cx-cart-item-list-row',
  templateUrl: './cart-item-list-row.component.html',
  styleUrl: 'cart-item-list-row.component.scss',
  providers: [CartItemContextSource, { provide: CartItemContext, useExisting: CartItemContextSource }],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CustomCartItemListRowComponent extends CustomCartItemComponent implements OnInit, OnDestroy {
  @ViewChild('inputShipDate', { read: ElementRef }) inputShipDate!: ElementRef;
  @Input() displayDates = true;
  @Input() displayValues = true;

  DEVICE_SIZE = DEVICE_SIZE;
  subscription = new Subscription();
  hasPoLine$ = this.cartPoLineService.get();
  isProductConfigured: boolean = false;

  minDate$ = new BehaviorSubject(new Date(new Date().setHours(0, 0, 0, 0)));
  blockedDates$ = new BehaviorSubject<Date[]>([]);
  fixProductName = fixProductName;
  formDate: UntypedFormGroup = this.fb.group({
    date: [null],
  });

  constructor(
    private fb: UntypedFormBuilder,
    private modalService: ModalService,
    private _cartItemContextSource: CartItemContextSource,
    private cartPoLineService: CartPoLineService,
    private activeCartService: CustomActiveCartService,
    private toasterService: ToasterService,
  ) {
    super(_cartItemContextSource);
  }

  ngOnInit() {
    this.populateShipDate();
    this.isProductConfigured = !!this.item.productConfigurationCode;
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  populateShipDate() {
    const requestedShipDate = this.item?.requestedShipDate ? new Date(this.item?.requestedShipDate) : undefined;

    if (requestedShipDate) {
      requestedShipDate.setDate(requestedShipDate.getDate());
      this.formDate.get('date')?.setValue(requestedShipDate);
    }

    if (this.readonly) {
      this.minDate$.next(new Date('01/01/1900'));
    }

    if (!this.readonly) {
      this.subscription.add(
        this.activeCartService.getActive().subscribe(cart => {
          const { holidays } = cart;

          const _holidays = (holidays || [])?.map((holiday: string) => {
            const date = new Date(holiday);
            date.setDate(date.getDate());
            return date;
          });

          this.blockedDates$.next(_holidays || []);
        }),
      );
    }

    this.subscription.add(
      this.formDate.get('date')?.valueChanges.subscribe(date => {
        const $input = this.inputShipDate.nativeElement.querySelector('input') as HTMLInputElement;
        const inputValue = $input.value;

        const _isDate = isDate(date);
        const dispatchEvent = inputValue === '' || _isDate;
        const value = _isDate ? date : inputValue;

        if (isEmpty(value)) {
          this.formDate.get('date')?.setValue(new Date());
          return;
        }

        dispatchEvent && this.updateShipDate(value);
      }),
    );
  }

  checkCounterReadOnly(item: any, readonly: any, options: any) {
    return !item.updateable || readonly || options.isSaveForLater || this.isProductConfigured;
  }

  checkMaxCounter(item: any) {
    return item.product?.stock?.stockLevel;
  }

  convertProduct(product: Product) {
    return of(product);
  }

  convertDateToString(date?: string | Date): string {
    return date ? new Date(date).toISOString().split('T')[0] : '';
  }

  openModalConfigDetails(entry: OrderEntry) {
    this.modalService.create({
      id: 'modal-config-details',
      title: 'Configuration Details',
      component: ModalConfigDetailsComponent,
      width: 1000,
      data: {
        entry,
      },
    });
  }

  openModalSeeNotes(entry: OrderEntry) {
    this.modalService.create({
      id: 'modal-see-notes',
      title: 'Notes',
      component: ModalSeeNotesComponent,
      width: 900,
      data: {
        entry,
        setNotes: (notes: string) => {
          this.setNotes(notes);
        },
      },
    });
  }

  updatePOLine($input: HTMLInputElement) {
    const poLine = $input.value;

    if (this.item.entryNumber !== undefined && this.item.poLine !== poLine) {
      this.activeCartService
        .updatePoLine(this.item.entryNumber, poLine)
        .pipe(take(1))
        .subscribe(data => {
          if (data.hasError) {
            this.toasterService.init({
              type: TOASTER_TYPE.DANGER,
              content: data.message || '',
            });
          } else {
            this.activeCartService.reloadActiveCart();
          }
        });
    }
  }

  updateShipDate(date: Date | string) {
    if (this.item.entryNumber !== undefined) {
      this.activeCartService
        .updateShipDate(this.item.entryNumber, date)
        .pipe(take(1))
        .subscribe(data => {
          if (data.hasError) {
            this.toasterService.init({
              type: TOASTER_TYPE.DANGER,
              content: data.message || '',
            });
          } else {
            this.activeCartService.reloadActiveCart();
          }
        });
    }
  }

  selectWarehouse(warehouse: Warehouse) {
    if (this.item.entryNumber !== undefined) {
      this.activeCartService
        .updateWarehouse(this.item.entryNumber, warehouse.warehouseCode!)
        .pipe(take(1))
        .subscribe(data => {
          if (data.hasError) {
            this.toasterService.init({
              type: TOASTER_TYPE.DANGER,
              content: data.message || '',
            });
          } else {
            this.activeCartService.reloadActiveCart();
          }
        });
    }
  }

  setNotes(notes: string) {
    if (this.item.entryNumber !== undefined) {
      this.activeCartService
        .updateNotes(this.item.entryNumber, notes)
        .pipe(take(1))
        .subscribe(data => {
          if (data.hasError) {
            this.toasterService.init({
              type: TOASTER_TYPE.DANGER,
              content: data.message || '',
            });
          } else {
            this.activeCartService.reloadActiveCart();
          }
        });
    }
  }

  handlerKeydownShipDate(event: KeyboardEvent) {
    const key = event.key.toLocaleLowerCase();

    if (key !== 'backspace' && key !== 'delete') {
      event.preventDefault();
      event.stopPropagation();
    }
  }
}
